import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { send } from '@actual-app/core/platform/client/connection';
import type { Query } from '@actual-app/core/shared/query';
import type { RuleConditionEntity } from '@actual-app/core/types/models';

import { TransactionListWithBalances } from '#components/mobile/transactions/TransactionListWithBalances';
import {
  useAbrirMovimiento,
  usePrevistosPagosFijos,
} from '#components/mobile/transactions/usePrevistosPagosFijos';
import { useDateFormat } from '#hooks/useDateFormat';
import { useTransactions } from '#hooks/useTransactions';
import { useTransactionsSearch } from '#hooks/useTransactionsSearch';
import * as queries from '#queries';
import * as bindings from '#spreadsheet/bindings';

type AllAccountTransactionsProps = {
  /** En lugar del saldo total (H59: la pestaña Movimientos). */
  cabecera?: ReactNode;
};

export function AllAccountTransactions({
  cabecera,
}: AllAccountTransactionsProps = {}) {
  return <TransactionListWithPreviews cabecera={cabecera} />;
}

function TransactionListWithPreviews({
  cabecera,
}: AllAccountTransactionsProps) {
  const { t } = useTranslation();
  const location = useLocation();
  // Filter conditions passed by drill-downs (e.g. report activity)
  const filterConditions = location?.state?.filterConditions || [];
  const isFiltered = filterConditions.length > 0;

  const makeRootTransactionsQuery = useCallback(
    () => queries.transactions().options({ splits: 'all' }).select('*'),
    [],
  );

  const [currentQuery, setCurrentQuery] = useState<Query | undefined>(() =>
    isFiltered ? undefined : makeRootTransactionsQuery(),
  );
  const [transactionsQuery, setTransactionsQuery] = useState<Query | undefined>(
    currentQuery,
  );

  useEffect(() => {
    let isStale = false;

    const applyFilters = async (conditions: RuleConditionEntity[]) => {
      const { filters: queryFilters } = await send(
        'make-filters-from-conditions',
        { conditions },
      );
      const rootQuery = makeRootTransactionsQuery();
      const query = rootQuery.filter({ $and: queryFilters });
      if (!isStale) {
        setCurrentQuery(query);
        setTransactionsQuery(query);
      }
    };

    const conditions = location?.state?.filterConditions;
    if (conditions?.length) {
      void applyFilters(conditions);
    }

    return () => {
      isStale = true;
    };
  }, [location.state, makeRootTransactionsQuery]);

  const {
    transactions,
    isPending: isTransactionsLoading,
    isFetchingNextPage: isLoadingMoreTransactions,
    fetchNextPage: fetchMoreTransactions,
  } = useTransactions({
    query: transactionsQuery,
  });
  const { previewTransactions, isLoading: isPreviewTransactionsLoading } =
    usePrevistosPagosFijos();

  const dateFormat = useDateFormat() || 'MM/dd/yyyy';

  const { isSearching, search: onSearch } = useTransactionsSearch({
    updateQuery: updateFn =>
      setTransactionsQuery(query => (query ? updateFn(query) : query)),
    resetQuery: () => setTransactionsQuery(currentQuery),
    dateFormat,
  });

  const onOpenTransaction = useAbrirMovimiento();

  const balanceBindings = useMemo(
    () => ({
      balance: bindings.hastaHoy(bindings.allAccountBalance()),
    }),
    [],
  );

  const transactionsToDisplay =
    !isSearching && !isFiltered
      ? // Do not render child transactions in the list, unless searching or filtering
        previewTransactions.concat(transactions.filter(t => !t.is_child))
      : transactions;

  return (
    <TransactionListWithBalances
      query={isSearching ? transactionsQuery : undefined}
      conSeleccionar
      isLoading={
        isSearching || isFiltered
          ? isTransactionsLoading
          : isPreviewTransactionsLoading
      }
      transactions={transactionsToDisplay}
      balance={balanceBindings.balance}
      isLoadingMore={isLoadingMoreTransactions}
      onLoadMore={fetchMoreTransactions}
      searchPlaceholder={t('Search All Accounts')}
      onSearch={onSearch}
      onOpenTransaction={onOpenTransaction}
      showMakeTransfer
      filtered={isFiltered}
      cabecera={cabecera}
    />
  );
}
